import { defineStore } from 'pinia'
import { ref } from 'vue'

import {
  dropSnapshot as libDrop,
  loadAll as libLoadAll,
  loadSnapshot as libLoad,
  saveSnapshot as libSave,
  type ResumableSnapshot,
} from '@/lib/resumable-runs'

/**
 * Pinia wrapper around the on-disk resumable-runs lib. Owns just the
 * reactive `ids` set so the picker's "weitermachen" v-if is cheap to
 * compute and re-renders when the training screen saves or drops a
 * snapshot.
 *
 * Mirrors `stores/runQueue.ts` in shape but doesn't install any lifecycle
 * listeners — these snapshots aren't uploaded, so there's no "flush on
 * network back" semantics. `install()` is called from `App.vue` once at
 * mount; it loads + TTL-purges the on-disk map and seeds `ids`.
 */
/** Cache namespaces that carry resumable snapshots on the patient device. */
const NAMESPACES = ['exercises', 'homework'] as const
const DEFAULT_NAMESPACE = 'exercises'

export const useResumableRunsStore = defineStore('resumableRuns', () => {
  /**
   * Per-namespace sets of exercise_ids that currently have a snapshot on
   * disk. The picker reads `has(id, namespace)` to decide whether to render
   * the "weitermachen" tab. `exercises` holds patient-authored exercises (and
   * therapist exercises in the embed build, which is a separate device);
   * `homework` holds therapist homework run on the patient device — kept apart
   * because the two id spaces collide. Mutated by `save` / `drop` / `install`.
   */
  const sets = ref<Record<string, Set<number>>>({ exercises: new Set(), homework: new Set() })
  let installed = false

  function setFor(namespace: string): Set<number> {
    return sets.value[namespace] ?? new Set()
  }

  // Vue 3 reactive Sets don't re-trigger watchers on mutation, so each
  // mutation replaces the inner set AND the outer record to force a re-render.
  function replaceSet(namespace: string, next: Set<number>): void {
    sets.value = { ...sets.value, [namespace]: next }
  }

  function has(exerciseId: number, namespace: string = DEFAULT_NAMESPACE): boolean {
    return setFor(namespace).has(exerciseId)
  }

  async function getSnapshot(
    exerciseId: number,
    namespace: string = DEFAULT_NAMESPACE,
  ): Promise<ResumableSnapshot | null> {
    return libLoad(exerciseId, namespace)
  }

  /** Save (or replace) a snapshot and reflect it in the reactive set. */
  async function save(snapshot: ResumableSnapshot, namespace: string = DEFAULT_NAMESPACE): Promise<void> {
    await libSave(snapshot, namespace)
    if (!setFor(namespace).has(snapshot.exercise_id)) {
      const next = new Set(setFor(namespace))
      next.add(snapshot.exercise_id)
      replaceSet(namespace, next)
    }
  }

  async function drop(exerciseId: number, namespace: string = DEFAULT_NAMESPACE): Promise<void> {
    await libDrop(exerciseId, namespace)
    if (setFor(namespace).has(exerciseId)) {
      const next = new Set(setFor(namespace))
      next.delete(exerciseId)
      replaceSet(namespace, next)
    }
  }

  /**
   * Load + TTL-purge each namespace's on-disk map, populate the sets.
   * Idempotent. Called by `App.vue` on mount.
   */
  async function install(): Promise<void> {
    if (installed) return
    installed = true
    const seeded: Record<string, Set<number>> = {}
    for (const namespace of NAMESPACES) {
      const map = await libLoadAll(namespace)
      seeded[namespace] = new Set(Object.keys(map).map((k) => Number.parseInt(k, 10)))
    }
    sets.value = seeded
  }

  return { sets, has, getSnapshot, save, drop, install }
})
